import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { ChevronLeft, Archive, Calendar, Users, Palette, Search, Download, AlertTriangle, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ArchiveView({ onBack }) {
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [summary, setSummary] = useState(null);
  const [campers, setCampers] = useState([]);
  const [transferReport, setTransferReport] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("list"); // list | campers | report

  const fetchYears = useCallback(async () => {
    try {
      const res = await fetch(`${API}/archive/years`);
      const data = await res.json();
      setYears(data.years || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchYears(); }, [fetchYears]);

  const loadSummary = async (year) => {
    setSelectedYear(year);
    setView("campers");
    setLoading(true);
    try {
      const [sumRes, campRes] = await Promise.all([
        fetch(`${API}/archive/${year}/summary`),
        fetch(`${API}/archive/${year}/campers`),
      ]);
      setSummary(await sumRes.json());
      const campData = await campRes.json();
      setCampers(campData.campers || []);
    } catch { toast.error("Failed to load archive"); }
    finally { setLoading(false); }
  };

  const loadTransferReport = async (year) => {
    setSelectedYear(year);
    setView("report");
    setLoading(true);
    try {
      const res = await fetch(`${API}/archive/transfer-report/${year}`);
      setTransferReport(await res.json());
    } catch { toast.error("Failed to load report"); }
    finally { setLoading(false); }
  };

  const handleArchiveCurrent = async () => {
    const year = new Date().getFullYear();
    if (!window.confirm(`Archive the current ${year} season? This saves a snapshot of all camper data.`)) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/archive/create/${year}`, { method: "POST" });
      const data = await res.json();
      if (data.status === "success") {
        toast.success(`Archived ${data.campers_archived} campers for ${year}`);
        fetchYears();
      } else {
        toast.error(data.message);
      }
    } catch { toast.error("Archive failed"); }
    finally { setLoading(false); }
  };

  const filtered = campers.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (c.first_name || "").toLowerCase().includes(q) || (c.last_name || "").toLowerCase().includes(q);
  });

  // ── Year List View ──
  if (view === "list") {
    return (
      <div className="max-w-2xl mx-auto px-3 py-4">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={onBack} className="text-gray-500 hover:text-gray-800" data-testid="archive-back">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <Archive className="h-5 w-5 text-gray-600" />
          <h1 className="text-lg font-bold text-gray-800">Season Archives</h1>
        </div>

        <Button onClick={handleArchiveCurrent} disabled={loading} className="w-full mb-6 bg-[#1e3a5f] hover:bg-[#2d5a87]" data-testid="archive-current-btn">
          <Archive className="h-4 w-4 mr-2" />
          Archive Current Season
        </Button>

        {years.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Archive className="h-10 w-10 mx-auto mb-3 text-gray-300" />
            <p className="text-sm">No archived seasons yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {years.map(year => (
              <div key={year} className="bg-white rounded-xl border border-gray-200 p-4" data-testid={`archive-year-${year}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <span className="font-bold text-gray-800 text-lg">{year} Season</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => loadSummary(year)} className="flex-1" data-testid={`view-archive-${year}`}>
                    <Users className="h-3.5 w-3.5 mr-1.5" /> View Campers
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => loadTransferReport(year)} className="flex-1" data-testid={`report-archive-${year}`}>
                    <Palette className="h-3.5 w-3.5 mr-1.5" /> Transfer Report
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── Camper List View ──
  if (view === "campers") {
    return (
      <div className="max-w-2xl mx-auto px-3 py-4">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => { setView("list"); setSelectedYear(null); }} className="text-gray-500 hover:text-gray-800">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-gray-800">{selectedYear} Archive</h1>
            {summary && <span className="text-xs text-gray-500">{summary.camper_count} campers &middot; {summary.test_results_count} tests</span>}
          </div>
        </div>

        {summary && (
          <div className="flex gap-1.5 flex-wrap mb-4">
            {Object.entries(summary.color_breakdown || {}).map(([color, count]) => (
              <span key={color} className="text-xs bg-gray-100 text-gray-700 rounded-full px-2.5 py-1 font-medium">
                {color}: {count}
              </span>
            ))}
          </div>
        )}

        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search archived campers..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 h-10 bg-white border-gray-300"
            data-testid="archive-search"
          />
        </div>

        {loading ? (
          <div className="text-center py-8 text-gray-400">Loading...</div>
        ) : (
          <div className="space-y-2">
            {filtered.slice(0, 100).map((c, i) => (
              <div key={i} className="bg-white rounded-lg border border-gray-200 px-3 py-2 flex items-center justify-between">
                <div>
                  <span className="font-medium text-gray-800 text-sm">{c.last_name}, {c.first_name}</span>
                  <span className="text-xs text-gray-400 ml-2">{c.division}</span>
                </div>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                  (c.swim_color || "").toLowerCase().includes("green") ? "bg-green-100 text-green-700" :
                  (c.swim_color || "").toLowerCase().includes("yellow") ? "bg-yellow-100 text-yellow-700" :
                  (c.swim_color || "").toLowerCase().includes("red") ? "bg-red-100 text-red-700" :
                  (c.swim_color || "").toLowerCase().includes("blue") ? "bg-blue-100 text-blue-700" :
                  "bg-gray-100 text-gray-500"
                }`}>{c.swim_color || "-"}</span>
              </div>
            ))}
            {filtered.length > 100 && <p className="text-center text-xs text-gray-400 py-2">Showing 100 of {filtered.length}</p>}
          </div>
        )}
      </div>
    );
  }

  // ── Transfer Report View ──
  if (view === "report") {
    const mismatches = (transferReport?.report || []).filter(r => r.status === "color_mismatch");
    const notEnrolled = (transferReport?.report || []).filter(r => r.status === "not_enrolled");

    return (
      <div className="max-w-2xl mx-auto px-3 py-4">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => { setView("list"); setSelectedYear(null); setTransferReport(null); }} className="text-gray-500 hover:text-gray-800">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-gray-800">{selectedYear} Transfer Report</h1>
            <span className="text-xs text-gray-500">Comparing archived colors with current data</span>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-8 text-gray-400">Loading...</div>
        ) : transferReport?.total_issues === 0 ? (
          <div className="text-center py-12 bg-green-50 border border-green-200 rounded-xl">
            <CheckCircle className="h-10 w-10 text-green-500 mx-auto mb-3" />
            <p className="font-semibold text-green-800">All colors transferred correctly</p>
            <p className="text-sm text-green-600 mt-1">No mismatches found</p>
          </div>
        ) : (
          <>
            <div className="flex gap-3 mb-4">
              <div className="flex-1 bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
                <div className="text-xl font-bold text-amber-700">{mismatches.length}</div>
                <div className="text-xs text-amber-600">Color Mismatches</div>
              </div>
              <div className="flex-1 bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
                <div className="text-xl font-bold text-gray-700">{notEnrolled.length}</div>
                <div className="text-xs text-gray-500">Not Enrolled</div>
              </div>
            </div>

            {mismatches.length > 0 && (
              <div className="mb-4">
                <h3 className="text-sm font-bold text-amber-700 mb-2 flex items-center gap-1.5">
                  <AlertTriangle className="h-3.5 w-3.5" /> Color Mismatches
                </h3>
                <div className="space-y-2">
                  {mismatches.map((r, i) => (
                    <div key={i} className="bg-white rounded-lg border border-amber-200 px-3 py-2">
                      <div className="font-medium text-sm text-gray-800">{r.name}</div>
                      <div className="flex items-center gap-2 mt-1 text-xs">
                        <span className="text-gray-500">{selectedYear}:</span>
                        <span className="font-bold">{r.archived_color}</span>
                        <span className="text-gray-400">&rarr;</span>
                        <span className="text-gray-500">Current:</span>
                        <span className="font-bold">{r.current_color || "-"}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {notEnrolled.length > 0 && (
              <div>
                <h3 className="text-sm font-bold text-gray-600 mb-2">Not Enrolled in Current Season ({notEnrolled.length})</h3>
                <div className="space-y-1 max-h-[300px] overflow-y-auto">
                  {notEnrolled.map((r, i) => (
                    <div key={i} className="bg-gray-50 rounded px-3 py-1.5 text-xs flex justify-between">
                      <span className="text-gray-700">{r.name}</span>
                      <span className="text-gray-400">{r.archived_color}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  }
}
